import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, Users, Mail, Phone, Calendar, MapPin, RefreshCw, MessageCircle, Trash2, Send, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Turn any phone input into a WhatsApp wa.me URL. Assumes UK if starting with 0.
const toWhatsAppUrl = (raw) => {
  if (!raw) return null;
  // Strip everything except digits and leading +
  let s = raw.trim();
  const hasPlus = s.startsWith('+');
  s = s.replace(/[^\d]/g, '');
  if (!s) return null;
  // If it starts with 0 (UK local format) and no country code, assume UK
  if (!hasPlus && s.startsWith('0')) s = '44' + s.slice(1);
  // If it starts with 44 already, keep
  return `https://wa.me/${s}`;
};

const WeddingFairStats = () => {
  const [params] = useSearchParams();
  const key = params.get('key') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [triggerListId, setTriggerListId] = useState('');

  const unsyncedCount = useMemo(
    () => (data?.submissions || []).filter((s) => !s.brevo_synced_at).length,
    [data]
  );

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${BACKEND_URL}/api/wedding-fair/submissions?key=${encodeURIComponent(key)}`);
      if (r.status === 403) { setError('Invalid admin key'); setLoading(false); return; }
      const d = await r.json();
      setData(d);
    } catch {
      setError('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const deleteRow = async (submission) => {
    const label = `${submission.name1} & ${submission.name2} (${submission.email})`;
    if (!window.confirm(`Delete this submission?\n\n${label}\n\nThis cannot be undone.`)) return;
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/wedding-fair/submissions/${encodeURIComponent(submission.id)}?key=${encodeURIComponent(key)}`,
        { method: 'DELETE' }
      );
      if (!r.ok) {
        window.alert('Delete failed. Try again.');
        return;
      }
      // Optimistic local removal so the UI feels instant
      setData((prev) => prev ? {
        count: prev.count - 1,
        submissions: prev.submissions.filter((s) => s.id !== submission.id),
      } : prev);
    } catch {
      window.alert('Delete failed. Try again.');
    }
  };

  const pushToBrevo = async () => {
    if (!unsyncedCount) return;
    const trimmedListId = triggerListId.trim();
    const listBit = trimmedListId ? `\n\nContacts will also be added to Brevo list #${trimmedListId} (fires that list's workflow).` : '';
    if (!window.confirm(
      `Push ${unsyncedCount} un-synced lead${unsyncedCount === 1 ? '' : 's'} to Brevo?\n\n` +
      `This will create/update a contact AND create a new Deal in the "Default" pipeline for each — just like the website form.` +
      listBit
    )) return;
    setPushing(true);
    setPushResult(null);
    try {
      const qs = new URLSearchParams({ key });
      if (trimmedListId) qs.set('trigger_list_id', trimmedListId);
      const r = await fetch(
        `${BACKEND_URL}/api/wedding-fair/push-to-brevo?${qs.toString()}`,
        { method: 'POST' }
      );
      const d = await r.json();
      if (!r.ok) {
        setPushResult({ ok: false, message: d.detail || 'Push failed' });
      } else {
        setPushResult({ ok: true, ...d });
        await load();
      }
    } catch (e) {
      setPushResult({ ok: false, message: 'Network error' });
    } finally {
      setPushing(false);
    }
  };

  useEffect(() => {
    document.title = 'Wedding Fair Submissions · Wedding Video Essex';
    if (!key) { setError('Missing admin key in URL (?key=...)'); setLoading(false); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  const csvUrl = `${BACKEND_URL}/api/wedding-fair/submissions.csv?key=${encodeURIComponent(key)}`;

  return (
    <div data-testid="wedding-fair-stats" className="min-h-screen" style={{ backgroundColor: '#1A1A1A' }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-7xl">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-gold text-xs uppercase tracking-widest font-semibold mb-2">Admin</p>
            <h1 className="font-serif text-4xl text-white mb-1">Wedding Fair Submissions</h1>
            <p className="text-white/50 text-sm">All leads captured through the /weddingfair funnel — newest first.</p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={load}
              disabled={loading}
              data-testid="wfs-refresh"
              className="inline-flex items-center gap-2 border border-white/20 text-white/80 hover:text-white hover:border-white/40 text-sm font-medium px-4 py-2.5 rounded-full transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <div className="flex items-center gap-0 rounded-full bg-white/[0.05] border border-white/10 pl-3 pr-1 py-1">
              <span className="text-white/40 text-[10px] uppercase tracking-widest">Trigger list #</span>
              <input
                type="number"
                inputMode="numeric"
                value={triggerListId}
                onChange={(e) => setTriggerListId(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="e.g. 23"
                data-testid="wfs-trigger-list-id"
                title="Optional Brevo list ID to also add contacts to. If that list is a workflow trigger, adding contacts here will fire the automation."
                className="w-16 bg-transparent text-white text-sm px-2 py-1.5 focus:outline-none placeholder:text-white/25"
              />
            </div>
            <button
              onClick={pushToBrevo}
              disabled={pushing || !unsyncedCount}
              data-testid="wfs-push-brevo"
              title={unsyncedCount ? `Push ${unsyncedCount} un-synced lead(s) to Brevo` : 'All leads already synced'}
              className="inline-flex items-center gap-2 bg-white text-black text-sm font-semibold px-5 py-2.5 rounded-full transition-all hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100"
            >
              <Send className={`w-4 h-4 ${pushing ? 'animate-pulse' : ''}`} />
              {pushing ? 'Pushing…' : unsyncedCount ? `Push ${unsyncedCount} to Brevo` : 'All synced'}
            </button>
            <a
              href={csvUrl}
              data-testid="wfs-download-csv"
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black text-sm font-semibold px-5 py-2.5 rounded-full transition-all hover:scale-[1.02]"
            >
              <Download className="w-4 h-4" /> Download CSV
            </a>
          </div>
        </div>

        {pushResult && (
          <div
            data-testid="wfs-push-result"
            className={`mb-6 rounded-2xl border px-5 py-4 text-sm ${
              pushResult.ok
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'
                : 'bg-red-500/10 border-red-500/30 text-red-200'
            }`}
          >
            {pushResult.ok ? (
              <>
                <strong>Brevo push complete.</strong> Synced {pushResult.synced} of {pushResult.attempted}
                {pushResult.failed ? ` — ${pushResult.failed} failed` : ''}. Contacts + Deals created in your "Default" pipeline.
                {pushResult.errors && pushResult.errors.length > 0 && (
                  <ul className="mt-2 list-disc list-inside text-red-200/80 text-xs">
                    {pushResult.errors.map((e, i) => (
                      <li key={i}>{e.email}: {e.error}</li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <strong>Push failed: {pushResult.message}</strong>
            )}
          </div>
        )}

        {loading && <p className="text-white/50">Loading submissions…</p>}
        {error && <p className="text-red-400 text-sm">{error}</p>}

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard icon={Users} label="Total Leads" value={data.count} />
              <StatCard
                icon={CheckCircle2}
                label="Synced to Brevo"
                value={data.submissions.filter((s) => s.brevo_synced_at).length}
              />
              <StatCard
                icon={Phone}
                label="With Phone"
                value={data.submissions.filter((s) => s.phone).length}
              />
              <StatCard
                icon={Calendar}
                label="Latest"
                value={data.submissions[0] ? formatDate(data.submissions[0].timestamp).split(',')[0] : '—'}
                small
              />
            </div>

            {/* Table */}
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden">
              <div className="grid px-4 py-3 border-b border-white/10 text-white/40 text-[10px] uppercase tracking-widest" style={{ gridTemplateColumns: '1.3fr 1.4fr 1.8fr 1.4fr 1.4fr 1.1fr 0.9fr 40px' }}>
                <div>When</div>
                <div>Couple</div>
                <div>Email</div>
                <div>Phone</div>
                <div>Venue (typed)</div>
                <div>Wedding Date</div>
                <div>Brevo</div>
                <div></div>
              </div>
              {data.submissions.length === 0 && (
                <p className="text-white/40 text-sm p-8 text-center">No submissions yet.</p>
              )}
              {data.submissions.map((s, i) => (
                <div key={s.id || i} className="grid items-center px-4 py-3 border-b border-white/[0.05] hover:bg-white/[0.02] transition-colors text-sm" style={{ gridTemplateColumns: '1.3fr 1.4fr 1.8fr 1.4fr 1.4fr 1.1fr 0.9fr 40px' }}>
                  <div className="text-white/50 text-xs">{formatDate(s.timestamp)}</div>
                  <div className="text-white">
                    {s.name1} <span className="text-white/40">&amp;</span> {s.name2}
                  </div>
                  <div className="text-white/80 text-xs break-all">
                    <a href={`mailto:${s.email}`} className="hover:text-gold">{s.email}</a>
                  </div>
                  <div className="text-white/80 text-xs">
                    {s.phone ? (
                      <div className="flex items-center gap-2">
                        <a href={`tel:${s.phone}`} className="hover:text-gold">{s.phone}</a>
                        {toWhatsAppUrl(s.phone) && (
                          <a
                            href={toWhatsAppUrl(s.phone)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`wfs-whatsapp-${s.id}`}
                            title="Open WhatsApp"
                            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#25D366]/15 hover:bg-[#25D366]/30 text-[#25D366] transition-colors"
                            aria-label={`WhatsApp ${s.name1}`}
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    ) : (
                      <span className="text-white/30">—</span>
                    )}
                  </div>
                  <div className="text-white/80 text-xs">
                    <span>{s.venue}</span>
                    {s.matched_slug && (
                      <a
                        href={`/venues/${s.matched_slug}`}
                        className="block text-gold/70 hover:text-gold text-[10px] uppercase tracking-widest mt-0.5"
                      >
                        → {s.matched_slug}
                      </a>
                    )}
                  </div>
                  <div className="text-white/70 text-xs">{s.wedding_date || <span className="text-white/30">—</span>}</div>
                  <div className="text-xs">
                    {s.brevo_synced_at ? (
                      <span
                        data-testid={`wfs-brevo-synced-${s.id}`}
                        title={`Synced ${formatDate(s.brevo_synced_at)}${s.brevo_deal_id ? ` · Deal ${s.brevo_deal_id}` : ''}`}
                        className="inline-flex items-center gap-1 text-emerald-400"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Synced
                      </span>
                    ) : (
                      <span
                        data-testid={`wfs-brevo-pending-${s.id}`}
                        className="inline-flex items-center gap-1 text-white/30"
                      >
                        Pending
                      </span>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => deleteRow(s)}
                      data-testid={`wfs-delete-${s.id}`}
                      title="Delete submission"
                      aria-label={`Delete ${s.name1} ${s.name2}`}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, small = false }) => (
  <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-5">
    <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest mb-2">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <p className={`font-serif text-white ${small ? 'text-lg' : 'text-3xl'}`}>{value}</p>
  </div>
);

export default WeddingFairStats;
